import { PanelsTopLeft } from "lucide-react";
import {
  createContext,
  type JSX,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { EmptyState, InspectorPanel, InspectorSection, KVRow } from "../design";

/**
 * Shell-level inspector dock. Any surface can publish a selection context
 * (title plus backend-prepared key/value rows) through this provider
 * without changing route or filters; the dock renders it in the AppShell
 * inspector slot. Open/closed state is a non-credential UI preference
 * persisted under the `jacqos.web_studio.ui.*` namespace; the published
 * selection itself is in-memory only and survives in-vertical navigation
 * because the provider lives above the route outlet.
 */
export interface ShellInspectorRow {
  label: string;
  value: string;
}

export interface ShellInspectorSelection {
  /** Mono title line (an entity or section id). */
  title: string;
  subtitle?: string | undefined;
  /** Caps section heading naming where the selection came from. */
  sectionLabel: string;
  rows: ShellInspectorRow[];
}

interface ShellInspectorValue {
  selection: ShellInspectorSelection | null;
  open: boolean;
  setOpen: (open: boolean) => void;
  publish: (selection: ShellInspectorSelection) => void;
  clear: () => void;
}

/** Non-protected UI preference key: dock visibility only, no session data. */
const INSPECTOR_STORAGE_KEY = "jacqos.web_studio.ui.inspector";

const ShellInspectorContext = createContext<ShellInspectorValue | null>(null);

function readStoredOpen(): boolean {
  try {
    const raw = window.localStorage.getItem(INSPECTOR_STORAGE_KEY);
    if (!raw) {
      return false;
    }
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null
      ? (parsed as Record<string, unknown>).open === true
      : false;
  } catch {
    return false;
  }
}

export function ShellInspectorProvider({ children }: { children: ReactNode }): JSX.Element {
  const [selection, setSelection] = useState<ShellInspectorSelection | null>(null);
  const [open, setOpenState] = useState<boolean>(() => readStoredOpen());

  useEffect(() => {
    try {
      window.localStorage.setItem(INSPECTOR_STORAGE_KEY, JSON.stringify({ open }));
    } catch {
      // Preference persistence is best-effort; the session state still applies.
    }
  }, [open]);

  const setOpen = useCallback((next: boolean) => setOpenState(next), []);
  const publish = useCallback((next: ShellInspectorSelection) => setSelection(next), []);
  const clear = useCallback(() => setSelection(null), []);

  const value = useMemo(
    () => ({ selection, open, setOpen, publish, clear }),
    [selection, open, setOpen, publish, clear],
  );

  return <ShellInspectorContext.Provider value={value}>{children}</ShellInspectorContext.Provider>;
}

export function useShellInspector(): ShellInspectorValue {
  const value = useContext(ShellInspectorContext);
  if (!value) {
    throw new Error("useShellInspector requires ShellInspectorProvider above the shell");
  }
  return value;
}

/**
 * The docked panel for the AppShell `inspector` slot. Renders the current
 * published selection, or a typed empty state naming how the dock fills.
 */
export function ShellInspectorDock(): JSX.Element {
  const { selection, setOpen } = useShellInspector();
  return (
    <InspectorPanel
      aria-label="Shell inspector"
      title={selection === null ? "Inspector" : selection.title}
      subtitle={selection?.subtitle}
      onClose={() => setOpen(false)}
    >
      {selection === null ? (
        <EmptyState
          icon={<PanelsTopLeft size={20} />}
          label="no selection"
          title="Nothing selected yet."
        >
          Selecting an inspectable row in any surface publishes its detail here without changing the
          route.
        </EmptyState>
      ) : (
        <InspectorSection label={selection.sectionLabel} count={selection.rows.length}>
          {selection.rows.map((row) => (
            <KVRow key={row.label} label={row.label} copyable wrap>
              {row.value}
            </KVRow>
          ))}
        </InspectorSection>
      )}
    </InspectorPanel>
  );
}
